// ── suppliers.js — 공급처 마스터 데이터 (V18) ─────────────────────────────
//
// 공급처(도매상·식자재 업체)를 셈핏 운영자가 관리.
// 공동구매 안건마다 공급처를 연결해서 협상/발주/정산 흐름의 시작점으로 사용.
//
// Firestore 구조: /suppliers/{id}
//   (전역 마스터 — admin만 쓰기, 모든 사용자 읽기)

import {
  collection, onSnapshot, query, orderBy,
  addDoc, doc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../db';

// ── 카테고리 (groupBuying의 GB_CATEGORIES와 align) ──
export const SUPPLIER_CATEGORIES = [
  { value: '야채',     label: '🥬 야채/과일' },
  { value: '육류',     label: '🥩 육류' },
  { value: '수산',     label: '🐟 수산물' },
  { value: '공산품',   label: '📦 공산품/소스' },
  { value: '주방용품', label: '🍳 주방용품' },
  { value: '음료',     label: '🥤 음료/주류' },
  { value: '포장재',   label: '🥡 포장재/일회용' },
  { value: '청소',     label: '🧴 청소/위생' },
  { value: '기타',     label: '기타' },
];

// ── 결제 조건 ──
export const PAYMENT_TERMS = [
  { value: 'prepaid',    label: '선결제' },
  { value: 'cod',        label: '현금/카드 (수령 시)' },
  { value: 'net7',       label: '7일 후 결제' },
  { value: 'net15',      label: '15일 후 결제' },
  { value: 'net30',      label: '월말 결제' },
  { value: 'other',      label: '기타' },
];

// ── 활성 상태 ──
export const SUPPLIER_STATUS = {
  active:   { label: '활성',     color: '#18181B', bg: '#F4F4F5' },
  inactive: { label: '비활성',   color: '#6b7280', bg: '#f3f4f6' },
  blocked:  { label: '거래중지', color: '#52525B', bg: '#F4F4F5' },
};

// ── 빈 공급처 템플릿 ──
export const emptySupplier = () => ({
  name: '',
  contactName: '',         // 담당자명
  phone: '',
  kakao: '',               // 카카오톡 ID/오픈채팅 URL
  email: '',
  bizNumber: '',           // 사업자등록번호
  categories: [],          // string[] from SUPPLIER_CATEGORIES.value
  region: '',              // 예: '서울 송파', '경기 광주'
  address: '',
  paymentTerms: 'cod',     // PAYMENT_TERMS.value
  minOrder: 0,             // 최소 발주 금액
  deliveryFee: 0,          // 배송비
  notes: '',               // 자유 메모
  status: 'active',        // SUPPLIER_STATUS key
  rating: 0,               // 0~5
  reviewCount: 0,
  createdAt: null,
  updatedAt: null,
  createdBy: '',           // admin uid
});

// ── Firestore CRUD ──

// 전체 공급처 구독
export function subscribeSuppliers(onUpdate, onError) {
  const q = query(collection(db, 'suppliers'), orderBy('name', 'asc'));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onUpdate(list);
  }, (err) => {
    if (onError) onError(err);
  });
}

// 공급처 추가
export async function addSupplier(data, adminUid) {
  const payload = {
    ...emptySupplier(),
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: adminUid || '',
  };
  const ref = await addDoc(collection(db, 'suppliers'), payload);
  return ref.id;
}

// 공급처 수정
export async function updateSupplier(id, patch) {
  await updateDoc(doc(db, 'suppliers', id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

// 공급처 삭제
export async function deleteSupplier(id) {
  await deleteDoc(doc(db, 'suppliers', id));
}

// ── 검색·필터 ──
export function filterSuppliers(list, { q = '', category = '', status = '' } = {}) {
  const term = String(q || '').trim().toLowerCase();
  return (list || []).filter(s => {
    if (status && s.status !== status) return false;
    if (category && !(s.categories || []).includes(category)) return false;
    if (term) {
      const hay = [s.name, s.contactName, s.phone, s.kakao, s.email, s.region, s.notes]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  });
}

// ── 정렬 ──
export function sortSuppliers(list) {
  const arr = Array.isArray(list) ? [...list] : [];
  return arr.sort((a, b) => {
    // 활성 우선
    if (a.status !== b.status) {
      if (a.status === 'active') return -1;
      if (b.status === 'active') return 1;
    }
    // rating 큰 순
    if ((b.rating || 0) !== (a.rating || 0)) return (b.rating || 0) - (a.rating || 0);
    // 이름순
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

// ── 카테고리 라벨 헬퍼 ──
export function categoryLabel(value) {
  return SUPPLIER_CATEGORIES.find(c => c.value === value)?.label || value;
}

// ── 카카오 오픈채팅 URL 정규화 ──
export function normalizeKakaoUrl(input) {
  if (!input) return '';
  const s = String(input).trim();
  if (!s) return '';
  if (s.startsWith('http')) return s;
  if (s.startsWith('open.kakao.com')) return 'https://' + s;
  return s; // 그냥 ID로 저장
}
